// P:\0PJ\synforge\frontend\app\settings\profile\page.tsx v6 2026.07.30 - sidebar removed (moved to layout.tsx), billing/data sections split into separate pages

'use client';

import { useState, useEffect, useRef } from 'react';
import { authAPI } from '@/lib/api';
import { Check, Eye, EyeOff, Lock } from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import { Country, State } from 'country-state-city';

type AccountTab = 'profile' | 'password';

export default function ProfilePage() {
  const { t } = useLocale();

  const [accountTab, setAccountTab] = useState<AccountTab>('profile');

  const [name, setName]       = useState('');
  const [bio, setBio]         = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  // 주소 — country 는 ISO 코드(예: "AU")로 저장한다. State 목록이 country 코드로
  //   조회되기 때문이다. 화면 표시/AI 프롬프트 주입 시에는 국가명(예: "Australia")으로
  //   변환해서 쓴다(백엔드 저장값도 사람이 읽는 이름 - AI 컨텍스트 질문과 형식을 맞춘다).
  const [countryCode, setCountryCode] = useState('');
  const [stateCode, setStateCode]     = useState('');
  const [city, setCity]               = useState('');
  const [postalCode, setPostalCode]   = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [addressSaved, setAddressSaved] = useState(false);

  const allCountries = Country.getAllCountries();
  const statesForCountry = countryCode ? State.getStatesOfCountry(countryCode) : [];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saved, setSaved]     = useState(false);

  // 비밀번호
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw]     = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const u = JSON.parse(stored);
        setName(u.name || u.username || '');
        setAvatarUrl(u.avatar_url || '');
      } catch {}
    }
    // 서버가 진짜 최신 값이다(localStorage 는 로그인 시점 스냅샷일 수 있음) -
    //   주소는 me() GET 으로 다시 받아 채운다. 저장은 국가명(문자열)이므로,
    //   State 드롭다운을 채우려면 그 이름으로 ISO 코드를 역으로 찾아야 한다.
    authAPI.me().then((res) => {
      const u = res.data;
      if (u.country) {
        const match = Country.getAllCountries().find((c) => c.name === u.country);
        if (match) setCountryCode(match.isoCode);
      }
      if (u.state_province) {
        // country 매칭 뒤에 실행되도록 약간 지연 없이도 동작 - State 목록 조회는
        //   countryCode 만 있으면 되고, 여기서는 이름만 맞춰 저장해 둔다.
        setStateCode(u.state_province);
      }
      setCity(u.city || '');
      setPostalCode(u.postal_code || '');
      setAddressLine(u.address_line || '');
    }).catch(() => {});
  }, []);

  const handleSave = (setter: (v: boolean) => void) => {
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const handleSaveProfile = async () => {
    try {
      const res = await authAPI.updateMe(name);
      const stored = localStorage.getItem('user');
      if (stored) {
        const u = JSON.parse(stored);
        localStorage.setItem('user', JSON.stringify({ ...u, name: res.data.name }));
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save profile:', err);
    }
  };

  const handleSaveAddress = async () => {
    try {
      const countryName = countryCode
        ? Country.getAllCountries().find((c) => c.isoCode === countryCode)?.name || ''
        : '';
      const stateName = (countryCode && stateCode)
        ? State.getStatesOfCountry(countryCode).find((s) => s.isoCode === stateCode)?.name || stateCode
        : stateCode;
      await authAPI.updateAddress({
        country: countryName,
        state_province: stateName,
        city,
        postal_code: postalCode,
        address_line: addressLine,
      });
      setAddressSaved(true);
      setTimeout(() => setAddressSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save address:', err);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await authAPI.uploadAvatar(file);
      setAvatarUrl(res.data.avatar_url);
      const stored = localStorage.getItem('user');
      if (stored) {
        const u = JSON.parse(stored);
        localStorage.setItem('user', JSON.stringify({ ...u, avatar_url: res.data.avatar_url }));
      }
    } catch (err) {
      console.error('Avatar upload failed:', err);
    }
  };

  const handleAvatarRemove = async () => {
    try {
      await authAPI.removeAvatar();
      setAvatarUrl('');
      const stored = localStorage.getItem('user');
      if (stored) {
        const u = JSON.parse(stored);
        localStorage.setItem('user', JSON.stringify({ ...u, avatar_url: '' }));
      }
    } catch (err) {
      console.error('Avatar remove failed:', err);
    }
  };

  const initial = name ? name.charAt(0).toUpperCase() : 'U';

  const strength = newPw.length === 0 ? 0
    : newPw.length < 6 ? 1
    : newPw.length < 10 ? 2
    : /[A-Z]/.test(newPw) && /[0-9]/.test(newPw) && /[^A-Za-z0-9]/.test(newPw) ? 4 : 3;
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColor = ['', 'bg-red-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];

  function Card({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
    return (
      <div className="space-y-5 pb-8 mb-8 border-b border-white/[0.05] last:border-0 last:mb-0 last:pb-0">
        <div>
          <h3 className="text-base font-semibold text-white tracking-tight">{title}</h3>
          {desc && <p className="text-xs text-white/30 mt-1">{desc}</p>}
        </div>
        {children}
      </div>
    );
  }

  function SaveButton({ onClick, saved }: { onClick: () => void; saved: boolean }) {
    return (
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onClick} className="bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors">
          {t('settings.profile.saveChanges')}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-green-400 text-xs">
            <Check size={13} /> {t('settings.profile.saved')}
          </span>
        )}
      </div>
    );
  }

  function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
    return (
      <input {...props} className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-purple-500/60 transition-colors" />
    );
  }

  function PasswordInput({ value, onChange, placeholder, show, onToggle }: {
    value: string; onChange: (v: string) => void; placeholder: string; show: boolean; onToggle: () => void;
  }) {
    return (
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 pr-10 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-purple-500/60 transition-colors"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[640px]">
      {/* Profile / Password 소탭 */}
      <div className="flex items-center gap-1 mb-6 bg-white/[0.03] rounded-xl p-1 w-fit">
        <button
          onClick={() => setAccountTab('profile')}
          className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            accountTab === 'profile' ? 'bg-purple-600 text-white' : 'text-white/40 hover:text-white/70'
          }`}
        >
          Profile
        </button>
        <button
          onClick={() => setAccountTab('password')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            accountTab === 'password' ? 'bg-purple-600 text-white' : 'text-white/40 hover:text-white/70'
          }`}
        >
          <Lock size={11} />
          Password
        </button>
      </div>

      {accountTab === 'profile' && (
        <div className="space-y-4">
          <Card title={t('settings.profile.avatar')} desc="Used across meetings and your public profile">
            <div className="flex items-center gap-4">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar" className="w-16 h-16 rounded-2xl object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-purple-600 flex items-center justify-center text-white text-2xl font-bold shrink-0">{initial}</div>
              )}
              <div className="space-y-2">
                <button onClick={() => fileInputRef.current?.click()} className="block text-sm text-purple-400 hover:text-purple-300 transition-colors">{t('settings.profile.uploadPhoto')}</button>
                {avatarUrl && (
                  <button onClick={handleAvatarRemove} className="block text-xs text-white/25 hover:text-white/50 transition-colors">{t('settings.profile.removePhoto')}</button>
                )}
              </div>
            </div>
          </Card>

          <Card title="Personal Information">
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">{t('settings.profile.fullName')}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">{t('settings.profile.bio')}</label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3}
                className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-purple-500/60 transition-colors resize-none" />
            </div>
            <SaveButton onClick={handleSaveProfile} saved={saved} />
          </Card>

          <Card title="Address" desc="Used to give the AI panel accurate country/region context (e.g. tax, interest rates) when your agenda doesn't specify one.">
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Country</label>
              <select
                value={countryCode}
                onChange={(e) => { setCountryCode(e.target.value); setStateCode(''); }}
                className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/60 transition-colors"
              >
                <option value="">Select a country</option>
                {allCountries.map((c) => (
                  <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                ))}
              </select>
            </div>

            {countryCode && statesForCountry.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">State / Province</label>
                <select
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500/60 transition-colors"
                >
                  <option value="">Select a state / province</option>
                  {statesForCountry.map((s) => (
                    <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">City</label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Postal Code</label>
              <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="Postal / ZIP code" />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Street Address</label>
              <Input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} placeholder="Street address" />
            </div>
            <SaveButton onClick={handleSaveAddress} saved={addressSaved} />
          </Card>
        </div>
      )}

      {accountTab === 'password' && (
        <div className="space-y-4">
          <Card title="Change Password">
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Current Password</label>
              <PasswordInput value={current} onChange={setCurrent} placeholder="********" show={showCurrent} onToggle={() => setShowCurrent(!showCurrent)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">New Password</label>
              <PasswordInput value={newPw} onChange={setNewPw} placeholder="********" show={showNew} onToggle={() => setShowNew(!showNew)} />
              {newPw.length > 0 && (
                <div className="mt-2 space-y-1">
                  <div className="flex gap-1">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= strength ? strengthColor[strength] : 'bg-white/10'}`} />
                    ))}
                  </div>
                  <p className={`text-[11px] ${strengthColor[strength].replace('bg-', 'text-')}`}>{strengthLabel[strength]}</p>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Confirm Password</label>
              <PasswordInput value={confirm} onChange={setConfirm} placeholder="********" show={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} />
              {confirm && newPw !== confirm && <p className="text-[11px] text-red-400 mt-1">Passwords do not match</p>}
            </div>
            <SaveButton onClick={() => handleSave(setPwSaved)} saved={pwSaved} />
          </Card>
        </div>
      )}
    </div>
  );
}